import styled, { css } from 'styled-components';
import { getColor, typeScale, spacingUnits, StyleVars } from 'core/utils';
import LazyLoadedImg from 'core/components/LazyLoadedImg';

const lineHeightBase = 1.28;

export const ListingAdminLink = styled.a`
	position: absolute;
	z-index: 1009;
	padding: ${typeScale(1)};
	top: ${typeScale(2)};
	left: ${typeScale(2)};
	display: flex;
	flex-direction: column;
	justify-content: center;
	background: ${getColor('cjAdmin')};
	border-radius: ${typeScale(0.5)};
	color: ${getColor('white')};
	cursor: alias;
	opacity: 0.65;
	transition: opacity 300ms ease-in-out;

	&:hover {
		color: ${getColor('white')};
		opacity: 1;
	}

	svg {
		height: ${typeScale(2.5)};
		width: ${typeScale(2.5)};
		path { fill: currentColor; }
	}
`;

const lbImageStyles = css`
	position: relative;
	width: 100%; /* We grow the image to fill the full width. Otherise, the "like" heart icon will float to the right. */
	height: ${({ height }) => height ? `${height}px` : '226px' };
	margin: 0;
	background-color: ${getColor('gray1')};
	background-size: cover;
	background-position: center center;
	object-fit: cover;
	border-radius: 8px;
	overflow: hidden;

	${props => props.$compact && css`
		max-height: 165px;
	`}
`;

export const StoreName = styled.p`
	display: block;
	margin: 0;
	margin-top: ${typeScale(-1.5)};
	margin-bottom: ${typeScale(1.5)};
	font-size: ${typeScale(1.7)};

	@media (max-width: ${StyleVars.screenXsm}) {
		font-size: ${typeScale(1.25)};
		margin: 3px 0px 0px;
		line-height: 14px;
	}
`;

export const ListingBoxImg = styled.img`
	${lbImageStyles}

	@media(max-width: ${StyleVars.screenXsm}) {
		height: 106px; /* Match BoxImage height (cat listings on SSR vs client) */
	}
`;

export const ListingBoxAltImg = styled(ListingBoxImg)`
	position: absolute;
	top: 0;
	left: 0;
	z-index: 1;
	opacity: 0;
	transition: opacity 0.3s;
`;

export const ListingBox = styled.div`
	min-width: 100px;
	max-width: calc(100vw - ${spacingUnits(2)});
	width: 100%;
	transform: rotateZ(0);

	@media (max-width: ${StyleVars.screenSm}) {
		margin: 0 auto;
	}

	&:hover ${ListingBoxAltImg} {
		opacity: 1;
	}
`;

export const StatusTag = styled.div`
	position: absolute;
	left: 0;
	top: -${typeScale(4)};
	height: ${typeScale(4)};
	line-height: 18px;
	font-weight: 600;
	font-size: 12px;
	letter-spacing: 2px;
	padding: ${typeScale(1)} ${typeScale(2)};
	text-transform: uppercase;
	z-index: 2;
	background: ${getColor('albescentWhite')};
	color: ${getColor('rustyNail')};
`;

export const DiscountLabelFlag = styled.div`
	position: absolute;
	top: 0;
	right: 0;
	width: ${typeScale(9)};
	height: ${typeScale(9)};
	padding: 0;

	&:after {
		position: absolute;
		width: 0;
		height: 0;
		border-top: ${typeScale(9)} solid ${getColor('offGreen')};
		border-left: ${typeScale(9)} solid transparent;
		z-index: 0;
		top: 0;
		content: "";
	}

	@media (max-width: ${StyleVars.screenXsm}) {
		width: ${typeScale(7)};
		height: ${typeScale(7)};

		&:after {
			border-top: ${typeScale(7)} solid ${getColor('offGreen')};
			border-left: ${typeScale(7)} solid transparent;
		}
	}
`;

export const DiscountLabelText = styled.div`
	position: relative;
	font-size: 11px;
	left: 1em;
	top: 2.4em;
	transform: translateY(-1em) rotate(45deg);
	z-index: 1;
	text-align: center;
	color: ${getColor('zuccini')};

	@media (max-width: ${StyleVars.screenXsm}) {
		font-size: 9px;
	}
`;

export const DiscountLabelFlagFirstFree = styled(DiscountLabelFlag)`
	&:after {
		position: absolute;
		width: 0;
		height: 0;
		border-top: ${typeScale(9)} solid #6366F1;
		border-left: ${typeScale(9)} solid transparent;
		z-index: 0;
		top: 0;
		content: "";
	}

	@media (max-width: ${StyleVars.screenXsm}) {
		width: ${typeScale(7)};
		height: ${typeScale(7)};

		&:after {
			border-top: ${typeScale(7)} solid #6366F1;
			border-left: ${typeScale(7)} solid transparent;
		}
	}
`;

export const DiscountLabelTextFirstFree = styled(DiscountLabelText)`
	position: relative;
	left: 15px;
	transform: translateY(-50%) rotate(45deg);
	z-index: 1;
	text-align: center;
	top: calc(50% - 12px);
	font-size: 11px;
	color: rgb(255, 255, 255);
	line-height: 1.1;

	@media (max-width: ${StyleVars.screenXsm}) {
		font-size: 10px;
		left: 11px;
		top: calc(50% - 11px);
		line-height: 11px;
	}
`;

export const ProductInfo = styled.div`
	flex: 1;
	display: flex;
	flex-direction: column;
	position: relative;
	overflow: visible;
	width: 100%;
	height: 100%;
`;

export const ProductMods = styled.div`
	position: relative;
`;

export const ProductDetails = styled.div`
	position: static; /* must have to make psuedo-link full sized */
	color: ${getColor('gray')};
	font-size: ${typeScale(2)};
	line-height: ${lineHeightBase};

	/* account for DiscountLabelFlag */
	${props => props.hasDiscount && css`
		${TitleLink} {
			padding-right: ${typeScale(4.5)};
		},
		${StoreName} {
			padding-right: ${typeScale(1.75)};
		}
	`}
`;

export const TitleLink = styled.a`
	color: ${getColor("gray")};
	text-decoration: none;
	display: inline-block;
	margin: ${spacingUnits(0.25)} 0 ${spacingUnits(0.5)};
	font-size: ${typeScale(2)};
	font-weight: 600;
	letter-spacing: 0.2px;
	line-height: ${lineHeightBase};
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
	max-width: 100%;
	transition: color 0.3s ease-in-out;

	@media (max-width: ${StyleVars.screenXsm}) {
		font-size: ${typeScale(1.75)};
		line-height: 16px;
		margin: 14px 0px -5px 0px;
		max-height: 32px;
		min-height: 16px;
	}

	&:before {
		position: absolute;
		top: 0;
		bottom: 0;
		right: 0;
		left: 0;
		z-index: 1;
		content: "";
		width: 100%;
		height: 100%;
		cursor: pointer;
	}

	&:active, &:focus, &:hover {
		text-decoration: none;
		color: ${getColor("gray")};
	}
`;

export const Description = styled.div`
	display: block;
	/* stylelint-disable */
	display: -webkit-box;
	-webkit-box-orient: vertical;
	-webkit-line-clamp: 6;
	/* stylelint-enable */
	overflow: hidden;
	text-overflow: ellipsis;
	vertical-align: top;
	margin: 8px 0 0px;
	line-height: @line-height-base;
	max-height: 6 * (@font-size-base * @line-height-base);
	line-clamp: 6;

	p {
		max-height: 110px;
		overflow: hidden;
		margin-left: 0px;
	}
`;

export const PriceContainer = styled.div`
	color: ${getColor('gray')};
	font-size: ${typeScale(2)};
	font-weight: 700;
	margin-top: 10px;

	span {
		text-decoration: line-through;
		color: #999999;
		margin-right: 0.5em;
	}

	@media (max-width: ${StyleVars.screenSm}) {
		font-size: ${typeScale(1.5)};
		margin-top: 5px;
	}
`;

export const BadgeImage = styled(LazyLoadedImg)`
	width: ${props => props.size || 40}px;
	height: ${props => props.size || 40}px;
	position: absolute;
	top: -15px;
	left: -15px;

	/* Admin gear icon is 1009 */
	z-index: 1010;

	/* just in case */
	max-width: 60px;
	max-height: 60px;
`;

export const MarketingBadge = styled.div`
	margin-top:	16px;
	font-size: 10px;
	font-weight: 700;
	display: block;

	@media (max-width: ${StyleVars.screenSm}) {
		margin-top:	10px;
		margin-bottom: -10px;
	}
`;

export const GiftBowWrapper = styled.div`
	position: relative;
	z-index: 2;
`;

export const GiftBowIconWrapper = styled.div`
	position: fixed;
`;
